// src/components/Strength/StrengthSummary.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { calculateHrTSSStrength } from '../../utils/hrTssStrengthUtils';
import '../styles/Summary.css';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const StrengthSummary = ({ fileId, ftp: ftpFromProps }) => {
  const { t } = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const convertSecondsToTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  useEffect(() => {
    if (!fileId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res             = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const baseSession     = res.data.session || {};
        const extendedSession = res.data.sessions?.[0] || {};
        const session         = { ...baseSession, ...extendedSession };
        const activity        = res.data.activity || {};
        const completed       = res.data.completed || {};
        const metrics         = res.data.metrics || {};
        const zonesTarget     = res.data.zones_target || {};

        const ftp = ftpFromProps !== undefined && ftpFromProps !== null
          ? ftpFromProps
          : (zonesTarget.functional_threshold_power !== undefined && zonesTarget.functional_threshold_power !== null
              ? zonesTarget.functional_threshold_power
              : t('strength.notAvailable'));

        const totalWork    = session.total_work !== undefined ? session.total_work : (metrics.trabajo_total !== '-' ? metrics.trabajo_total : 0);
        const movementTime = session.total_timer_time || session.total_elapsed_time || completed.duracion || 0;
        const avgHR        = session.avg_heart_rate || metrics.ritmo_cardiaco?.avg || 0;
        const thresholdHR  = zonesTarget.threshold_heart_rate || metrics.ritmo_cardiaco?.umbral || 145;
        const tss          = calculateHrTSSStrength(avgHR, thresholdHR, movementTime);
        const normalize    = (value) => (value === '-' || value === undefined ? 0 : value);

        setData({
          date:             new Date(activity.timestamp || session.timestamp).toLocaleString(),
          sport:            `${session?.sport || ''}${session?.sub_sport ? ' - ' + session.sub_sport : ''}`,
          duration:         convertSecondsToTime(movementTime),
          movementDuration: convertSecondsToTime(session.total_timer_time || 0),
          elapsedTime:      convertSecondsToTime(session.total_elapsed_time || 0),
          calories:         session.total_calories || completed.calorias || '-',
          avgHR:            normalize(avgHR),
          maxHR:            normalize(session.max_heart_rate || metrics.ritmo_cardiaco?.max),
          trainingEffect:   session.total_training_effect || completed.te || '-',
          anaerobicEffect:  session.total_anaerobic_effect !== undefined ? session.total_anaerobic_effect : (completed.tea ?? '-'),
          totalWork:        normalize(totalWork),
          avgWeight:        session.avg_weight || completed.peso_promedio || '-',
          totalReps:        normalize(session.total_repetitions),
          totalSets:        session.total_sets || completed.series_totales || '-',
          avgTemp:          session.avg_temperature || '-',
          maxTemp:          session.max_temperature || '-',
          totalCycles:      session.total_cycles || '-',
          numLaps:          session.num_laps || '-',
          ftp,
          maxHeartRateTarget: zonesTarget.max_heart_rate || '-',
          thresholdHeartRate: thresholdHR,
          tss: isFinite(tss) ? tss : '-',
        });
      } catch (err) {
        console.error('❌ Error al cargar los datos:', err);
        setError(t('strength.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fileId, ftpFromProps, t]);

  if (loading) return <div>{t('strength.loading')}</div>;
  if (error)   return <div>{error}</div>;
  if (!data)   return <div>{t('strength.noData')}</div>;

  return (
    <div className="summary-container">
      <h4 className="mb-3">{t('strength.summaryTitle')}</h4>
      <div className="summary-grid">
        <div className="summary-item"><strong><i className="bi bi-calendar-event"></i> {t('strength.date')}</strong><p>{data.date}</p></div>
        <div className="summary-item"><strong><i className="bi bi-droplet-half"></i> {t('strength.sport')}</strong><p>{data.sport}</p></div>
        <div className="summary-item"><strong><i className="bi bi-clock-history"></i> {t('strength.totalTime')}</strong><p>{data.duration}</p></div>
        <div className="summary-item"><strong><i className="bi bi-play-circle"></i> {t('strength.movingTime')}</strong><p>{data.movementDuration}</p></div>
        <div className="summary-item"><strong><i className="bi bi-clock-fill"></i> {t('strength.elapsedTime')}</strong><p>{data.elapsedTime}</p></div>
        <div className="summary-item"><strong><i className="bi bi-fire"></i> {t('strength.calories')}</strong><p>{data.calories} kcal</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-pulse"></i> {t('strength.avgHR')}</strong><p>{data.avgHR} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-fill"></i> {t('strength.maxHR')}</strong><p>{data.maxHR} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-thermometer-half"></i> {t('strength.tempAvg')}</strong><p>{data.avgTemp} °C</p></div>
        <div className="summary-item"><strong><i className="bi bi-thermometer-high"></i> {t('strength.tempMax')}</strong><p>{data.maxTemp} °C</p></div>
        <div className="summary-item"><strong><i className="bi bi-arrow-repeat"></i> {t('strength.totalCycles')}</strong><p>{data.totalCycles}</p></div>
        <div className="summary-item"><strong><i className="bi bi-layers"></i> {t('strength.numLaps')}</strong><p>{data.numLaps}</p></div>
        <div className="summary-item"><strong><i className="bi bi-activity"></i> {t('strength.tte')}</strong><p>{data.trainingEffect}</p></div>
        <div className="summary-item"><strong><i className="bi bi-graph-up-arrow"></i> {t('strength.tea')}</strong><p>{data.anaerobicEffect}</p></div>
        <div className="summary-item"><strong><i className="bi bi-beaker"></i> {t('strength.hrTss')}</strong><p>{isFinite(data.tss) ? `${data.tss}` : t('strength.notAvailable')}</p></div>
        <div className="summary-item"><strong><i className="bi bi-lightning-charge"></i> {t('strength.ftp')}</strong><p>{data.ftp}</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart"></i> {t('strength.maxHRTarget')}</strong><p>{data.maxHeartRateTarget} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart"></i> {t('strength.thresholdHR')}</strong><p>{data.thresholdHeartRate} bpm</p></div>
      </div>
    </div>
  );
};

export default StrengthSummary;
